import { TenantId } from '@pelicargo/tenants';
import { convertFileToBase64 } from '@pelicargo/ui';
import { modelUtils } from '@pelicargo/utils';
import { useCallback } from 'react';

import { trpc } from '../../config/trpc';

const { CUSTOMER_ASSET_MAX_BYTES, customerAssetMimeTypes } = modelUtils.customerTheme;

type CustomerAssetSlot = modelUtils.customerTheme.CustomerAssetSlot;

type UseUploadCustomerAsset = {
  isUploading: boolean;
  upload: (slot: CustomerAssetSlot, file: File) => Promise<string>;
};

const MAX_MB = Math.floor(CUSTOMER_ASSET_MAX_BYTES / 1024 / 1024);

/**
 * Uploads one file for an asset slot and resolves to the public URL to store in the theme.
 *
 * Re-checking size and type client-side is not redundant with the SM method's checks: it avoids
 * base64-ing megabytes onto the wire only to be refused, and it puts the message next to the
 * control that caused it instead of in a request failure. The server still decides.
 */
export const useUploadCustomerAsset = (tenantId?: TenantId): UseUploadCustomerAsset => {
  const { mutateAsync, isLoading } = trpc.uploadTenantCustomerAsset.useMutation();

  const upload = useCallback(
    async (slot: CustomerAssetSlot, file: File) => {
      if (!tenantId) throw new Error('Pick a tenant before uploading an asset');

      if (file.size > CUSTOMER_ASSET_MAX_BYTES) {
        throw new Error(`That file is larger than the ${MAX_MB}MB limit`);
      }

      const accepted = customerAssetMimeTypes(slot);
      if (!accepted.includes(file.type)) {
        throw new Error(`The ${slot} slot accepts ${accepted.join(', ')}`);
      }

      const { url } = await mutateAsync({
        tenant_id: tenantId,
        slot,
        filename: file.name,
        content: await convertFileToBase64(file),
        content_type: file.type,
      });

      return url;
    },
    [mutateAsync, tenantId],
  );

  return { isUploading: isLoading, upload };
};
